import { getAuthenticatedClient } from "../supabaseClient";
import { parseCSV } from "../utils/parseCsv";
import { remap_chargerCSV_items } from "../utils/remapItems";
import { phasesDataList } from "./defaultapi";
import { chargerDataFormatted } from "../utils/chargerDataFormatted";
import fetchData from "../utils/fetchData";

const updatePhaseField = async (field, value, id) => {
  const supabase = getAuthenticatedClient();

  const updateFields = { [field]: value };

  const { data, error } = await supabase
    .from("phases")
    .update(updateFields)
    .eq("id", id)
    .select();

  if (error) {
    throw new Error(`Failed to update data: ${error.message}`);
  }

  return data;
};

const addPhase = async (body) => {
  const supabase = getAuthenticatedClient();
  const {
    user_id,
    site,
    year,
    loan_amount,
    trenching_costs,
    upgrade_cost_utility,
    upgrade_cost_customer,
    procurement_management_cost,
    estimated_public_works_engineering_costs,
    capital_planning_funding,
    port_less_than_10_kw,
    port_10_20_kw,
    port_25_kw,
    port_180_200_kw,
    incentives,
    master_access_incentives,
    other_costs,
    other_costs_description,
  } = body;

  // Perform data validation
  if (!user_id) {
    return { error: "Missing required field: userID" };
  }

  const { data, error } = await supabase
    .from("phases")
    .insert({
      user_id,
      site,
      year,
      loan_amount,
      trenching_costs,
      upgrade_cost_utility,
      upgrade_cost_customer,
      procurement_management_cost,
      estimated_public_works_engineering_costs,
      capital_planning_funding,
      incentives,
      port_less_than_10_kw,
      port_10_20_kw,
      port_25_kw,
      port_180_200_kw,
      master_access_incentives,
      other_costs,
      other_costs_description,
    })
    .select();

  if (error) {
    return { error: error };
  }
  return data[0];
};

const deletePhase = async (id) => {
  const supabase = getAuthenticatedClient();

  const response = await supabase.from("phases").delete().eq("id", id);

  if (response.error) {
    return { error: response.error };
  }

  return response;
};

// Replace the phases data with the new data in the CSV file if the site name is the same as the site name in Fleet Data.
const uploadPhasesCSV = async (formdata, deleteData = false) => {
  const supabase = getAuthenticatedClient();

  const file = formdata.get("file");
  const userId = formdata.get("userId");

  if (!file) {
    return { error: "No file uploaded." };
  }

  try {
    let { results } = await parseCSV(file);

    // Remap some of the items
    results = remap_chargerCSV_items(results);

    for (const item of results) {
      item.user_id = userId;
    }
    results = chargerDataFormatted(results);

    const jsonData = JSON.stringify(results);
    const jsonObject = JSON.parse(jsonData);

    let obj = jsonObject[0];
    let errors = [];

    // Check for required fields (excluding id and user_id as they're auto-generated)
    const requiredFields = phasesDataList.filter(
      (field) => field !== "id" && field !== "user_id" && field !== "ports",
    );

    requiredFields.forEach((key) => {
      // Check if the key exists in the object, accounting for potential whitespace/control chars
      const keyExists = Object.keys(obj).some(
        (objKey) =>
          objKey.trim() === key.trim() || objKey.replace(/\r$/, "") === key,
      );

      if (!keyExists) {
        errors.push(`missing key: ${key}`);
      }
    });

    if (errors.length > 0) {
      return { error: errors };
    }

    const dataResponse = await fetchData(userId, "fleetData");
    const dataControlsResponse = dataResponse?.controls?.domiciles;

    if (!Array.isArray(dataControlsResponse)) {
      return {
        error:
          "Charger CSV format is incorrect. Missing controls domiciles list.",
      };
    }

    const updatedJsonObject = jsonObject
      .map((item) => {
        // Create a new object to store the updated item
        const newItem = {};

        // Iterate over phasesDataList and add the key to newItem if it exists in item, otherwise add it as null
        phasesDataList.forEach((key) => {
          if (key !== "id") {
            // Find the matching key in the item, accounting for whitespace/control chars
            const matchingKey = Object.keys(item).find(
              (itemKey) =>
                itemKey.trim() === key.trim() ||
                itemKey.replace(/\r$/, "") === key,
            );

            newItem[key] = matchingKey ? item[matchingKey] : null;
          }
        });

        return newItem;
      })
      .filter((item) => dataControlsResponse.includes(item.site));

    if (updatedJsonObject.length === 0) {
      return {
        message:
          "There is no charger data to update because there is no site name matching",
      };
    } else {
      let existingPhases = [];
      if (deleteData) {
        const { data: existingData, error: existingError } = await supabase
          .from("phases")
          .select("*")
          .eq("user_id", userId);

        if (existingError) {
          return { error: existingError.message };
        }

        existingPhases = existingData || [];
      }

      if (deleteData) {
        const { error: deleteError } = await supabase
          .from("phases")
          .delete()
          .eq("user_id", userId)
          .select();
        if (deleteError) {
          return { error: deleteError.message };
        }
      }

      const restoreExistingPhases = async () => {
        if (!deleteData || existingPhases.length === 0) {
          return null;
        }

        const restorePayload = existingPhases.map((row) => {
          const rest = { ...row };
          delete rest.id;
          return rest;
        });

        const { error: restoreError } = await supabase
          .from("phases")
          .insert(restorePayload);

        return restoreError;
      };

      const { data, error } = await supabase
        .from("phases")
        .insert(updatedJsonObject)
        .select();

      if (error) {
        const restoreError = await restoreExistingPhases();
        if (restoreError) {
          return {
            error: `${error.message}. Restore failed: ${restoreError.message}`,
          };
        }
        return { error: error.message };
      }

      if (data?.length === 0) {
        const restoreError = await restoreExistingPhases();
        if (restoreError) {
          return {
            error: `Error uploading file. Restore failed: ${restoreError.message}`,
          };
        }
        return { error: "Error uploading file" };
      }

      return { message: "Phases file uploaded successfully!" };
    }
  } catch (error) {
    return { error: error.message || "Error processing CSV file" };
  }
};

export { updatePhaseField, addPhase, deletePhase, uploadPhasesCSV };
